import { useCallback, useEffect, useId, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ChipInteractionHintStore {
	/** Whether the user has dismissed the discovery pulse by clicking a chip. Persisted. */
	dismissed: boolean;
	/**
	 * Ids of the hint-eligible chips currently visible in the viewport, in the order they became
	 * visible. The first entry holds the pulse.
	 */
	claimants: string[];

	dismiss: () => void;
	register: (id: string) => void;
	unregister: (id: string) => void;
}

export const useChipInteractionHintStore = create<ChipInteractionHintStore>()(
	persist(
		(set) => ({
			dismissed: false,
			claimants: [],

			dismiss: () => set({ dismissed: true }),
			// Returning the current state unchanged makes redundant calls true no-ops: zustand skips
			// notifying subscribers (and the persist write) when the updater returns the same object.
			register: (id) =>
				set((state) => (state.claimants.includes(id) ? state : { claimants: [...state.claimants, id] })),
			unregister: (id) =>
				set((state) =>
					state.claimants.includes(id) ? { claimants: state.claimants.filter((claimant) => claimant !== id) } : state,
				),
		}),
		{
			name: 'chip-interaction-hint',
			partialize: (state) => ({ dismissed: state.dismissed }),
		},
	),
);

/**
 * Dismisses the chip interaction discovery pulse. Every mounted chip updates immediately, and the
 * dismissal persists in this browser across sessions. Idempotent.
 */
export function dismissChipInteractionHint(): void {
	useChipInteractionHintStore.getState().dismiss();
}

export interface ChipInteractionHintRole {
	/** Whether this chip should show the pulsing attention ring. */
	pulse: boolean;
	/** Callback ref for the chip's root element, used to track its viewport visibility. */
	observe: (node: HTMLElement | null) => void;
}

export interface UseChipInteractionHintOptions {
	/** Whether this chip may currently claim the pulse. */
	eligible: boolean;
}

/**
 * Tracks the pulse role of one chip instance. Until the pulse is dismissed, exactly one eligible
 * chip visible in the viewport is told to pulse — the longest-visible one — and when it leaves the
 * viewport (by scrolling or unmounting) the pulse passes to another visible chip.
 *
 * @returns The hint role, with:
 * - pulse: whether this chip should show the pulsing attention ring.
 * - observe: callback ref that must be attached to the chip's root element.
 */
export function useChipInteractionHint({ eligible }: UseChipInteractionHintOptions): ChipInteractionHintRole {
	const id = useId();
	const [element, setElement] = useState<HTMLElement | null>(null);
	const register = useChipInteractionHintStore((state) => state.register);
	const unregister = useChipInteractionHintStore((state) => state.unregister);
	const dismissed = useChipInteractionHintStore((state) => state.dismissed);

	// Claiming the pulse tracks this chip's viewport visibility, which only an
	// IntersectionObserver bound to the rendered element can observe. Dismissal is permanent, so
	// once dismissed there is nothing left to arbitrate and observation stops entirely.
	useEffect(() => {
		if (!eligible || dismissed || element === null) {
			return;
		}

		let intersecting = false;

		const evaluate = () => {
			// A chip that can't receive pointer events — disabled, or an inert decorative copy
			// (possibly via an ancestor, since `pointer-events` inherits) — can't demonstrate the
			// interactions, so it must not hold the pulse.
			const interactive = getComputedStyle(element).pointerEvents !== 'none';
			if (intersecting && interactive) {
				register(id);
			} else {
				unregister(id);
			}
		};

		const intersectionObserver = new IntersectionObserver(([entry]) => {
			intersecting = entry?.isIntersecting === true;
			evaluate();
		});
		intersectionObserver.observe(element);

		// Interactivity can change with no intersection event — e.g. the chip is dynamically
		// disabled while it stays on-screen — so re-evaluate on the element's own attribute changes.
		const mutationObserver = new MutationObserver(evaluate);
		mutationObserver.observe(element, { attributes: true, attributeFilter: ['class', 'style', 'disabled'] });

		return () => {
			intersectionObserver.disconnect();
			mutationObserver.disconnect();
			unregister(id);
		};
	}, [id, eligible, dismissed, element, register, unregister]);

	const holdsPulse = useChipInteractionHintStore((state) => state.claimants[0] === id);

	const observe = useCallback((node: HTMLElement | null) => {
		setElement(node);
	}, []);

	return { pulse: eligible && !dismissed && holdsPulse, observe };
}
